import { reactive, computed, getCurrentInstance, ComponentInternalInstance, ref, Ref } from '@vue/composition-api';

import { dispatchRouter } from '@/renderer/scripts/utils/tools/dispatchRouter';
import i18n, { i18n_t } from '@/locales/i18n';
import smh from '@/renderer/scripts/smh';
import tips, { MsgError } from '@/renderer/scripts/utils/tips';
import store from '@/renderer/store';
import user from '@/renderer/scripts/utils/user';

type InvitationDetail = {
  userNickname: string;
  userAvatar: string;
  orgName: string;
  orgLogo: string;
  orgId: number;
  showOrgNameInUI: boolean | undefined;
  groupName: string;
  expireTime: string;
  hasJoined: boolean;
  isExpired: boolean;
  type: string;
  typeName: string;
};
type CoStatus = Ref<
  | ''
  | 'waitingForJoining' // 等待加入
  | 'alreadyInGroup' // 已经加入群组或者企业
  | 'addSuccess' // 加入企业/群组/临时成员成功
  | 'differentOrgForbidden' // 不能跨企业加入
  | 'userLimitReached' // 成员达到上限
  | 'invitationDisabled' // 邀请失效
  | 'InvitationGroupNotFound' // 邀请的群组不存在
  | 'InvitingOrganizationServiceTimeExpired' // 邀请的企业过期
  | 'alreadyTemporary' // 临时成员已经加入
>;
type GetNewOrgInfoData = {
  error: null | Object;
  orgInfo: {} | CoFile.OrgItem;
};
type Handler = {
  coStatus: Ref<string>;
  invitationDetail: InvitationDetail;
  methods: {
    getInvitation: () => Promise<void>;
    joinTemporary: () => Promise<void>;
    joinGroup: () => Promise<void>;
    joinOrg: () => Promise<void>;
    getNewOrgInfo: () => Promise<GetNewOrgInfoData>;
    loginToNewOrg: () => void;
  };
};

export default function invitationHandler(): Handler {
  const currComponent = getCurrentInstance() as ComponentInternalInstance;
  const vm = currComponent.proxy;
  const invitationDetail = reactive({
    loading: true,
    userNickname: '',
    userAvatar: '',
    orgName: '',
    orgLogo: '',
    showOrgNameInUI: undefined,
    orgId: -1,
    groupName: '',
    expireTime: '',
    isExpired: false,
    hasJoined: false,
    type: '',
    typeName: '',
  });
  const isLogined = computed(() => store.getters.isLogined);
  const coStatus: CoStatus = ref('');

  // const groupId = ref(-1);
  const invitationCode = vm.$route.params.code as string;
  const methods = {
    // 获取邀请码信息
    async getInvitation() {
      try {
        const res = isLogined.value
          ? await smh.userAPI.getInvitationInfoWithUserInfo({
              code: invitationCode,
            })
          : await smh.userAPI.getInvitationInfo({
              code: invitationCode,
            });
        const {
          allowExternalUser,
          hasJoined,
          expireTime,
          expired,
          organization,
          group,
          invitor,
          orgId,
          userLimitReached,
          type,
          currentOrgUserCount,
          orgUserCountLimit,
        } = res.data;
        Object.assign(invitationDetail, {
          userNickname: invitor.nickname,
          userAvatar: invitor.avatar,
          expireTime,
          isExpired: expired,
          hasJoined,
          type,
          typeName: type === 'group' ? i18n_t('群组') : i18n_t('企业'),
        });
        let _userLimitReached = userLimitReached;
        const { name, logo, showOrgNameInUI } = organization;
        Object.assign(invitationDetail, {
          orgName: name,
          orgLogo: logo,
          showOrgNameInUI,
          orgId,
        });
        if (type === 'group') {
          Object.assign(invitationDetail, {
            groupName: group.name,
          });
        } else {
          // 企业人数已满
          const noHeadCount = currentOrgUserCount === orgUserCountLimit;
          _userLimitReached = typeof userLimitReached === 'undefined' ? noHeadCount : userLimitReached;
        }
        const differentOrgForbidden = !allowExternalUser && store.getters.isLogined && orgId !== store.getters.orgId;
        // groupId.value = group.id;
        if (hasJoined) {
          if (type === 'temporary' && isLogined.value && store.getters.userBaseInfo.isTemporary) {
            coStatus.value = 'alreadyTemporary';
          } else {
            // 优先判断是否已经加入了企业
            coStatus.value = 'alreadyInGroup';
          }
        } else if (differentOrgForbidden) {
          // 是否跨企业不能使用邀请码
          coStatus.value = 'differentOrgForbidden';
        } else if (_userLimitReached) {
          // 邀请码使用已达上限
          coStatus.value = 'userLimitReached';
        } else {
          coStatus.value = 'waitingForJoining';
        }
      } catch (e: any) {
        if (e.code === 'InvitationGroupNotFound') {
          // 可能群组已被删除
          coStatus.value = 'InvitationGroupNotFound';
          // 邀请的群组已过期
        } else if (e.code === 'InvitingOrganizationServiceTimeExpired') {
          coStatus.value = 'InvitingOrganizationServiceTimeExpired';
        } else {
          coStatus.value = 'invitationDisabled';
        }
        tips.error(e as MsgError);
      }
      Object.assign(invitationDetail, {
        loading: false,
      });
    },
    // 加入临时成员
    async joinTemporary() {
      try {
        await smh.userAPI.joinTemporary({
          code: invitationCode,
        });
        coStatus.value = 'addSuccess';
        tips.success(i18n_t('加入成功'));
      } catch (e) {
        tips.error(e as MsgError);
      }
    },
    // 加入群组
    async joinGroup() {
      try {
        await smh.userAPI.joinGroup({
          code: invitationCode,
        });
        coStatus.value = 'addSuccess';
        reporter.report('group_join_finished', {
          group_name: invitationDetail.groupName,
          result: 1,
        });
        tips.success(i18n_t('加入成功'));
      } catch (e) {
        reporter.report('group_join_finished', {
          group_name: invitationDetail.groupName,
          result: 0,
        });
        tips.error(e as MsgError);
      }
    },
    // 加入企业
    async joinOrg() {
      try {
        await smh.userAPI.joinOrg({
          code: invitationCode,
        });
        coStatus.value = 'addSuccess';
        tips.success(i18n_t('加入成功'));
      } catch (e) {
        tips.error(e as MsgError);
      }
    },
    // 获取新加入的企业信息
    async getNewOrgInfo() {
      let orgInfo: {} | CoFile.OrgItem = {};
      let error = null;
      try {
        // 获取所有企业列表
        const res = await smh.userAPI.getUserOrg();
        orgInfo = res.data.find(item => item.id === invitationDetail.orgId) as CoFile.OrgItem;
      } catch (e: any) {
        logger.smh(e);
        error = e;
      }
      return {
        error,
        orgInfo,
      };
    },
    // 登录进新企业
    async loginToNewOrg() {
      const { error, orgInfo } = await methods.getNewOrgInfo();
      if (error) {
        tips.error(i18n_t('获取新加入的企业信息失败，请稍后重试'));
        return;
      }
      // 当没查询到最新企业时 兜底进入当前已登录的企业
      if (!orgInfo) {
        dispatchRouter({
          name: 'workbench',
        });
        return;
      }
      try {
        const newOrgInfo = orgInfo as CoFile.OrgItem;
        if (newOrgInfo.ipLimitEnabled) {
          tips.error(i18n_t('你已成功加入该企业，但当前网络已被企业管理员设定为不可登录使用，需登录请联系管理员'));
          return;
        }
        const { id, libraryId, expired } = orgInfo as CoFile.OrgItem;
        await smh.userAPI.loginOrg({
          organizationId: id,
        });
        // 默认企业版
        const editionConfig = newOrgInfo?.extensionData?.editionConfig;
        const editionFlag = editionConfig?.editionFlag || 'enterprise';
        const orgChannel = newOrgInfo?.extensionData?.channelFlag;
        await user.setOrgAndLogin({
          organizationId: id,
          multiOrg: true,
          libraryId,
          expired,
          editionFlag,
          editionConfig,
          orgChannel,
        });
        dispatchRouter({
          name: 'workbench',
        });
      } catch (e: any) {
        tips.error(e);
      }
    },
  };
  return {
    coStatus,
    invitationDetail,
    methods,
  };
}
